/**
 * The Disposal page's Companies tab: every approved disposal company, its portal status and its
 * ten paid modules (§49). Paelode staff activate a company once it has paid, switch modules on
 * and off, and suspend or reactivate it with a reason (migration 0234). A SUSPENDED company's
 * modules are read-only until it is reactivated. Contacts are the primary contact email only.
 * Billing lives on the Billing page, linked here.
 */
import * as React from 'react';
import { useUrlNumber, useUrlParam } from '../../lib/url-state';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { DisposalCompanyContacts } from './DisposalCompanyContacts';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import {
  COMPANY_PAGE_SIZE,
  useActivateDisposalCompany,
  useDisposalCompanies,
  useSetDisposalCompanyStatus,
  useSetDisposalModules,
  type DisposalCompanyRow,
  type DisposalModuleKey,
  type DisposalPortalStatus,
  type DisposalStatusAction,
} from './hooks';
import {
  DISPOSAL_MODULES,
  PORTAL_STATUS_LABELS,
  STATUS_REASON_MAX,
  STATUS_REASON_MIN,
  disposalErrorMessage,
  modulesOn,
  portalStatusTone,
  statusReasonOk,
} from './disposal-labels';
import { formatNumber } from '@lodex-pro/shared/number-format';

/** Local-by-context date (DEC-004 — never render UTC). */
function fmtDate(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const STATUS_FILTERS: ReadonlyArray<{ id: DisposalPortalStatus | null; label: string }> = [
  { id: null, label: 'All' },
  { id: 'PAYMENT_PENDING', label: 'Payment pending' },
  { id: 'ACTIVE', label: 'Active' },
  { id: 'SUSPENDED', label: 'Suspended' },
];

export function DisposalCompaniesTab(): React.JSX.Element {
  // The status filter, the page and the open company live in the URL (lib/url-state.ts).
  const [statusParam, setStatusParam] = useUrlParam('companyStatus', '', ['offset', 'open']);
  const status = (statusParam === '' ? null : statusParam) as DisposalPortalStatus | null;
  const [offset, setOffset] = useUrlNumber('offset', ['open']);
  const [openParam, setOpenParam] = useUrlParam('open', '', []);
  const openId = openParam === '' ? null : openParam;
  const setOpenId = (id: string | null): void => setOpenParam(id ?? '');
  const { data, isLoading, isError, refetch } = useDisposalCompanies(status, offset);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const open = items.find((c) => c.id === openId) ?? null;

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {STATUS_FILTERS.map((f) => (
          <Button
            key={f.label}
            size="sm"
            variant={status === f.id ? 'default' : 'outline'}
            onClick={() => {
              // One URL write: the status, with the page and the open company cleared.
              setStatusParam(f.id ?? '');
            }}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {isLoading && <p style={subText}>Loading disposal companies…</p>}
      {isError && (
        <p style={{ color: 'var(--color-error)', fontSize: '14px' }}>
          Failed to load disposal companies.{' '}
          <button
            type="button"
            onClick={() => void refetch()}
            style={{
              textDecoration: 'underline',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              color: 'inherit',
            }}
          >
            Retry
          </button>
        </p>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <p style={subText}>No disposal companies{status === null ? '' : ' with this status'}.</p>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Tenant</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Portal</TableHead>
              <TableHead>Activated</TableHead>
              <TableHead>Modules</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`disposal-company-${c.id}`}>
                <TableCell style={{ fontSize: '13px' }}>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  {c.legalName !== null && c.legalName !== c.name && (
                    <div style={subText}>{c.legalName}</div>
                  )}
                </TableCell>
                <TableCell style={{ fontSize: '13px' }}>{c.tenantSlug}</TableCell>
                <TableCell style={{ fontSize: '13px' }}>{c.primaryContactEmail ?? '—'}</TableCell>
                <TableCell>
                  <Pill
                    label={PORTAL_STATUS_LABELS[c.portalStatus]}
                    tone={portalStatusTone(c.portalStatus)}
                  />
                </TableCell>
                <TableCell style={{ fontSize: '13px' }}>{fmtDate(c.activatedAt)}</TableCell>
                <TableCell style={{ fontSize: '13px' }}>{modulesOn(c.modules)} of 10</TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setOpenId(openId === c.id ? null : c.id)}
                  >
                    {openId === c.id ? 'Close' : 'Manage'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {total > COMPANY_PAGE_SIZE && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
          <Button
            size="sm"
            variant="outline"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - COMPANY_PAGE_SIZE))}
          >
            Previous
          </Button>
          <span style={subText}>
            {formatNumber(offset + 1)}–{formatNumber(Math.min(offset + COMPANY_PAGE_SIZE, total))}{' '}
            of {formatNumber(total)}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={offset + COMPANY_PAGE_SIZE >= total}
            onClick={() => setOffset(offset + COMPANY_PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      )}

      {open !== null && <CompanyPanel company={open} />}
    </div>
  );
}

/** One company: activate it, switch its modules, and link to its billing. */
export function CompanyPanel({ company }: { company: DisposalCompanyRow }): React.JSX.Element {
  const [confirmActivate, setConfirmActivate] = React.useState(false);
  const [turningOff, setTurningOff] = React.useState<DisposalModuleKey | null>(null);
  const [statusAction, setStatusAction] = React.useState<DisposalStatusAction | null>(null);
  const activate = useActivateDisposalCompany(company.id, () => {
    toast.success(`${company.name} is active. Its portal sign-in email is on its way.`);
    setConfirmActivate(false);
  });
  const setModules = useSetDisposalModules(company.id, () => {
    setTurningOff(null);
  });
  const active = company.portalStatus === 'ACTIVE';
  const busy = setModules.isPending;

  React.useEffect(() => {
    if (setModules.error != null) toast.error(disposalErrorMessage(setModules.error));
  }, [setModules.error]);
  React.useEffect(() => {
    if (activate.error != null) toast.error(disposalErrorMessage(activate.error));
  }, [activate.error]);

  const toggle = (key: DisposalModuleKey, on: boolean): void => {
    // Switching on takes effect at once. Switching off asks first: it changes this month's
    // billing snapshot (the DISPOSAL_MODULES meter counts what is on when it is read).
    if (!on) {
      setTurningOff(key);
      return;
    }
    setModules.mutate({ [key]: true });
  };
  const offLabel = DISPOSAL_MODULES.find((m) => m.key === turningOff)?.label ?? '';

  return (
    <section
      data-testid="disposal-company-panel"
      style={{
        marginTop: '16px',
        padding: '16px',
        borderRadius: 'var(--radius-card)',
        backgroundColor: 'var(--color-bg-secondary)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
        <div>
          <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>{company.name}</h3>
          <p style={{ ...subText, marginTop: '2px' }}>
            {PORTAL_STATUS_LABELS[company.portalStatus]}
            {company.activatedAt !== null ? ` since ${fmtDate(company.activatedAt)}` : ''} ·{' '}
            {company.primaryContactEmail ?? 'no contact email'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          {company.portalStatus === 'PAYMENT_PENDING' && (
            <Button size="sm" onClick={() => setConfirmActivate(true)}>
              Activate…
            </Button>
          )}
          {company.portalStatus === 'ACTIVE' && (
            <Button size="sm" variant="outline" onClick={() => setStatusAction('suspend')}>
              Suspend…
            </Button>
          )}
          {company.portalStatus === 'SUSPENDED' && (
            <Button size="sm" onClick={() => setStatusAction('reactivate')}>
              Reactivate…
            </Button>
          )}
          <Link
            to={`/billing/customers/${company.tenantId}`}
            style={{ fontSize: '13px', alignSelf: 'center' }}
          >
            Billing
          </Link>
        </div>
      </div>
      <DisposalCompanyContacts companyId={company.id} />

      {company.portalStatus === 'SUSPENDED' && (
        <p style={{ ...subText, marginTop: '12px' }} data-testid="disposal-suspended-note">
          This company is suspended: its users can’t sign in, and its modules and users aren’t
          billed. Reactivate it to change its modules.
        </p>
      )}
      {company.portalStatus === 'PAYMENT_PENDING' && (
        <p style={{ ...subText, marginTop: '12px' }}>
          Modules can be switched once the company is active.
        </p>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '8px',
          marginTop: '12px',
        }}
      >
        {DISPOSAL_MODULES.map((m) => (
          <label
            key={m.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              opacity: active ? 1 : 0.6,
            }}
          >
            <input
              type="checkbox"
              aria-label={m.label}
              checked={company.modules[m.key]}
              disabled={!active || busy}
              onChange={(e) => toggle(m.key, e.target.checked)}
            />
            {m.label}
          </label>
        ))}
      </div>

      <Dialog open={confirmActivate} onOpenChange={setConfirmActivate}>
        <DialogContent style={{ maxWidth: '460px' }}>
          <DialogHeader>
            <DialogTitle>Activate {company.name}?</DialogTitle>
          </DialogHeader>
          <p style={{ fontSize: '13px', margin: 0 }}>
            Its disposal portal opens, its facilities are linked to it, and{' '}
            {company.primaryContactEmail ?? 'its contact'} is emailed a sign-in link. All ten
            modules start off.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmActivate(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="disposal-activate-confirm"
              disabled={activate.isPending}
              onClick={() => activate.mutate({})}
            >
              {activate.isPending ? 'Activating…' : 'Activate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={turningOff !== null}
        onOpenChange={(o) => {
          if (!o) setTurningOff(null);
        }}
      >
        <DialogContent style={{ maxWidth: '460px' }}>
          <DialogHeader>
            <DialogTitle>Turn off {offLabel}?</DialogTitle>
          </DialogHeader>
          <p style={{ fontSize: '13px', margin: 0 }}>
            {company.name} loses {offLabel} at once. This changes this month’s billing snapshot: the
            module count is read when the month is billed.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTurningOff(null)}>
              Keep it on
            </Button>
            <Button
              type="button"
              data-testid="disposal-module-off-confirm"
              disabled={busy}
              onClick={() => {
                if (turningOff !== null) setModules.mutate({ [turningOff]: false });
              }}
            >
              Turn off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {statusAction !== null && (
        <StatusDialog
          company={company}
          action={statusAction}
          onClose={() => setStatusAction(null)}
        />
      )}
    </section>
  );
}

/** Suspend or reactivate, after a reason (10–500 characters) and a confirm. */
function StatusDialog({
  company,
  action,
  onClose,
}: {
  company: DisposalCompanyRow;
  action: DisposalStatusAction;
  onClose: () => void;
}): React.JSX.Element {
  const [reason, setReason] = React.useState('');
  const mutation = useSetDisposalCompanyStatus(company.id, action, () => {
    toast.success(
      action === 'suspend' ? `${company.name} is suspended.` : `${company.name} is active again.`,
    );
    onClose();
  });
  const ok = statusReasonOk(reason);
  const suspend = action === 'suspend';

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent style={{ maxWidth: '460px' }} data-testid="disposal-status-dialog">
        <DialogHeader>
          <DialogTitle>
            {suspend ? 'Suspend' : 'Reactivate'} {company.name}?
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (ok) mutation.mutate({ reason: reason.trim() });
          }}
        >
          <p style={{ fontSize: '13px', margin: '0 0 12px' }}>
            {suspend
              ? 'Its users can’t sign in to the disposal portal until it is reactivated. Its modules and users stop counting for billing from the next daily snapshot. Modules stay as they are.'
              : 'Its users can sign in again, and its modules and users count for billing again from the next daily snapshot. Modules come back as they were.'}
          </p>
          <FormRow label="Reason" htmlFor="dispStatusReason" required>
            <textarea
              id="dispStatusReason"
              style={{ ...fieldStyle, minHeight: '80px', resize: 'vertical' }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={`Kept on the audit record (${String(STATUS_REASON_MIN)}+ characters)`}
              maxLength={STATUS_REASON_MAX}
            />
          </FormRow>
          {mutation.error != null && (
            <p role="alert" style={{ fontSize: '13px', color: 'var(--color-error)' }}>
              {disposalErrorMessage(mutation.error)}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="disposal-status-confirm"
              variant={suspend ? 'destructive' : 'default'}
              disabled={!ok || mutation.isPending}
            >
              {suspend
                ? mutation.isPending
                  ? 'Suspending…'
                  : 'Suspend'
                : mutation.isPending
                  ? 'Reactivating…'
                  : 'Reactivate'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
